<template>
  <InputSelect
    ref="inputSelect"
    v-model="selectedOptions"
    :clearable="clearable"
    data-intercom-target="stream-picker"
    data-testid="stream-picker"
    :disabled="disabled"
    :dropdown-text="dropdownText"
    emit-value
    :hide-dropdown-button="useKeywordSearch"
    :multiple="!single"
    new-design
    option-label="label"
    option-prop="value"
    :optional="optional"
    :options="filteredListOptions"
    :placeholder="inputPlaceholder"
    :popper-width="popperWidth"
    :prevent-clear-input-on-submit="useKeywordSearch"
    :selected-text="selectedText"
    :teleport="teleport"
    :use-input="useKeywordSearch"
    :validations="validations"
    @click="$emit('click')"
    @dropdown-closed="resetChanges"
    @filter="onFilter"
  >
    <template
      v-if="useKeywordSearch"
      #empty
    >
      <div />
    </template>

    <template
      v-if="requiresPrependSlot"
      #prepend
    >
      <div
        v-if="single && selectedOptions"
        v-show="selectedOptions"
        class="stream-icon no-shrink col-auto"
        :style="{ backgroundColor: iconColor }"
      />
      <QIcon
        v-else-if="useKeywordSearch"
        color="ds-denim-4"
        name="ion-search"
        size="xs"
      />
    </template>
    <template
      v-if="!useKeywordSearch"
      #beforeOptions
    >
      <div
        class="before-options"
        @click.stop
      >
        <InputText
          v-model="filter"
          borderless
          class="input"
          clearable
          :debounce="250"
          new-design
          :placeholder="$t('stream_picker.search')"
          tabindex="-1"
          @focus="$emit('search-focus')"
        >
          <template #prepend>
            <ImageIcon
              class="text-ds-denim-4"
              fallback-icon="ion-search"
            />
          </template>
        </InputText>
        <slot name="optionsText">
          <div
            v-if="!single && maxSelected > 0"
            class="text-ds-denim-6 q-pa-sm stream-dropdown-description-font-size bold"
          >
            {{ $t("stream_picker.max_selected", { maxSelected }) }}
          </div>
        </slot>
      </div>
    </template>
    <template #option="{ option, selected, active }">
      <QIntersection class="intersection">
        <div
          class="list-option row full-width no-wrap cursor-pointer items-center"
          :class="{
            active: active,
            selected: selected && single,
            'group-option': option.isGroup,
          }"
          :data-testid="
            option.isGroup
              ? 'stream-picker-option-group'
              : 'stream-picker-option'
          "
          @click="$emit('option-click', option)"
        >
          <slot
            v-if="option.disabled"
            name="disabled"
          />
          <div v-if="option.isTag">
            <BaseIcon
              icon="tag"
              :style="{ color: option.color }"
            />
          </div>
          <div
            v-else
            class="stream-icon no-shrink col-auto"
            :style="{ backgroundColor: option.color }"
          />
          <div
            class="text-label ellipsis col-grow col-shrink q-pl-sm overflow-hidden"
          >
            {{ option.label }}
          </div>
          <slot
            :active="active"
            :hide-list="hideList"
            name="append"
            :option="option"
            :selected="selected"
          />
          <div
            v-if="!single"
            class="checkbox-left no-shrink col-auto"
          >
            <Checkbox
              v-if="!option.isGroup"
              :class="`color-${option.checkboxColor}` || checkboxColor"
              :model-value="selected"
              new-design
              @click="$emit('checkbox-toggled', option.label)"
            />
          </div>
        </div>
      </QIntersection>
    </template>
    <template
      v-if="!single"
      #afterOptions
    >
      <div class="row ok-row q-pa-xs border-ds-denim-2 justify-end">
        <BaseButton
          class="full-width"
          color="primary"
          data-testid="stream-picker-confirm-button"
          :disabled="invalid"
          :label="$t('global.confirm')"
          new-design
          @click.stop="onConfirm"
        />
        <StreemTooltip v-if="invalid">
          {{ $t("stream_picker.min_selected", { minSelected }) }}
        </StreemTooltip>
      </div>
    </template>
  </InputSelect>
</template>

<script>
import { keyBy } from "lodash-es";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseIcon,
  ImageIcon,
  InputSelect,
  InputText,
  StreemTooltip,
} from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { streamTypes } from "shared/constants";
import { useStreamsStore } from "shared/stores/streams";

export default {
  name: "StreamPicker",
  components: {
    BaseButton,
    BaseIcon,
    Checkbox,
    InputSelect,
    InputText,
    ImageIcon,
    StreemTooltip,
  },
  props: {
    modelValue: {
      type: [Array, Number, String],
      default: null,
    },
    validations: {
      type: Array,
      default: () => [],
    },
    optional: {
      type: Boolean,
    },
    sharedStreams: {
      type: Array,
      default: () => [],
      validator: (streamOptions) =>
        streamOptions.every(
          (streamOption) => streamOption.id && streamOption.label
        ),
    },
    minSelected: {
      type: Number,
      default: 1,
    },
    maxSelected: {
      type: Number,
      default: 12,
    },
    popperWidth: {
      type: [Number, null],
      default: 300,
    },
    selectedText: {
      type: Function,
      default: null,
    },
    checkboxColor: {
      type: String,
      default: "primary",
    },
    single: {
      type: Boolean,
    },
    includeSocialBookmarkStreams: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    keywordSearch: {
      type: Boolean,
    },
    socialStreams: {
      type: Boolean,
    },
    hideBookmarkStreams: {
      type: Boolean,
    },
    disabledStreamIds: {
      type: Array,
      default: () => [],
    },
    teleport: {
      type: String,
      default: null,
    },
  },
  emits: [
    "click",
    "search-focus",
    "checkbox-toggled",
    "confirm",
    "update:modelValue",
    "cancel",
    "option-click",
    "update:filter",
  ],
  setup(props) {
    const streamsStore = useStreamsStore();

    const {
      groupedTraditionalStreams,
      groupedSocialStreams,
      groupedSocialBookmarkStreams,
    } = streamsStore;

    const streamOptions = computed(() => {
      const sharedStreams = keyBy(props.sharedStreams, "id");
      const accessibleStreams = new Set();
      const options = [];

      const groupedStreams = props.socialStreams
        ? groupedSocialStreams()
        : groupedTraditionalStreams();

      groupedStreams
        .filter((group) => {
          if (props.hideBookmarkStreams) {
            return group.stream_type !== streamTypes.bookmarkStream;
          }

          return true;
        })
        .forEach((group) => {
          const groupOption = {
            value: group.id,
            label: group.label,
            color: group.color,
            isGroup: true,
            isTag: group.stream_type === streamTypes.bookmarkStream,
            options: [],
          };

          group.streams.forEach((stream) => {
            groupOption.options.push({
              value: stream.id,
              label: stream.label,
              isTag: stream.type === streamTypes.bookmarkStream,
              color: stream.color || group.color,
              primary: stream.primary,
            });

            accessibleStreams.add(stream.id);
          });

          options.push(groupOption);
        });

      const sharedStreamOptions = [];

      Object.values(sharedStreams).forEach((stream) => {
        if (!accessibleStreams.has(stream.id)) {
          sharedStreamOptions.push({
            value: stream.id,
            label: stream.label,
            color: stream.color,
          });
        }
      });

      if (
        props.includeSocialBookmarkStreams &&
        groupedSocialBookmarkStreams().length
      ) {
        groupedSocialBookmarkStreams().forEach((group) => {
          const groupOption = {
            value: group.id,
            label: group.label,
            color: group.color,
            isGroup: true,
            isTag: true,
            options: [],
          };

          group.streams.forEach((stream) => {
            groupOption.options.push({
              value: stream.id,
              label: stream.label,
              isTag: true,
              color: stream.color || group.color,
            });
          });

          options.push(groupOption);
        });
      }

      const sharedGroup = {
        value: 0,
        label: getLocaleText("stream_picker.shared_report_streams"),
        color: null,
        isGroup: true,
        options: sharedStreamOptions,
      };

      options.unshift(sharedGroup);

      return options;
    });

    return {
      streamOptions,
    };
  },
  data() {
    return {
      filter: "",
      localSelected: [],
      unsavedChanges: false,
    };
  },
  computed: {
    requiresPrependSlot() {
      return (this.single && this.selectedOptions) || this.useKeywordSearch;
    },
    inputPlaceholder() {
      return this.useKeywordSearch
        ? this.$t("stream_picker.keyword_search_placeholder")
        : "";
    },
    selectableOptions() {
      return this.streamOptions.flatMap(({ options }) => options);
    },
    selectedValues() {
      return (this.unsavedChanges ? this.localSelected : this.modelValue) || [];
    },
    selectedOptions: {
      get() {
        if (this.single) {
          return this.selectableOptions.find(
            (option) => this.selectedValues === option.value
          )?.value;
        }

        return this.selectableOptions.filter((option) =>
          this.selectedValues.includes(option.value)
        );
      },
      set(value) {
        if (this.single) {
          this.emitChanges(value);
        } else {
          this.localSelected = value || [];
          this.unsavedChanges = true;
        }
      },
    },
    dropdownText() {
      if (this.single) {
        if (this.modelValue) {
          return this.selectedOptions.label;
        }

        if (this.useKeywordSearch) {
          return this.filter;
        }

        return this.socialStreams
          ? this.$t("stream_picker.select_social_stream")
          : this.$t("stream_picker.select_mention_stream");
      }

      const selected = this.selectedOptions?.length;

      if (this.socialStreams) {
        return selected
          ? this.$t("stream_picker.selected_social_stream", {
              count: selected,
            })
          : this.$t("stream_picker.select_social_stream", { count: 2 });
      }

      return selected
        ? this.$t("stream_picker.selected_mention_stream", {
            count: selected,
          })
        : this.$t("stream_picker.select_mention_stream", { count: 2 });
    },
    filteredListOptions() {
      const filter = this.filter.toLowerCase();

      const filteredListOptions = [];

      this.streamOptions.forEach((group) => {
        const groupOptions = this.filterOptions(group.options, filter);

        if (groupOptions.length) {
          filteredListOptions.push(
            { ...group, notSelectable: true },
            ...groupOptions
          );
        }
      });

      return filteredListOptions;
    },
    invalid() {
      return this.single
        ? !this.selectedValues
        : this.minSelected && this.selectedValues.length < this.minSelected;
    },
    useKeywordSearch() {
      return this.single && this.keywordSearch;
    },
    iconColor() {
      if (!this.single) return "";

      const selectedOption = this.selectableOptions.find(
        (option) => this.selectedValues === option.value
      );

      return selectedOption ? selectedOption.color : "";
    },
  },
  methods: {
    filterOptions(options, filter) {
      const filteredOptions = [];

      options.forEach((option) => {
        if (option.label.toLowerCase().includes(filter)) {
          filteredOptions.push({
            ...option,
            disabled: this.isDisabled(option),
          });
        }
      });

      return filteredOptions;
    },
    isDisabled(option) {
      if (this.disabledStreamIds.includes(option.value)) {
        return true;
      }

      return this.single
        ? false
        : this.maxSelected &&
            this.selectedValues.length >= this.maxSelected &&
            !this.isSelected(option);
    },
    isSelected(option) {
      return this.single
        ? this.selectedOptions === option
        : this.selectedOptions.includes(option);
    },
    onConfirm() {
      this.$emit("confirm");

      if (this.unsavedChanges) {
        this.emitChanges(this.localSelected);
      } else {
        this.$refs.inputSelect.hideList();
      }
    },
    emitChanges(value) {
      this.$emit("update:modelValue", value);
      this.unsavedChanges = false;
      this.$refs.inputSelect.hideList();
    },
    resetChanges() {
      if (this.unsavedChanges) {
        this.$emit("cancel");
      }

      this.localSelected = [];
      this.unsavedChanges = false;
    },
    onFilter(value) {
      this.filter = value;

      this.$emit("update:filter", value);
    },
    /** @public */
    focus() {
      this.$refs.inputSelect.focus();
    },
    /** @public */
    validate() {
      return this.$refs.inputSelect.validate();
    },
    hideList() {
      this.$refs.inputSelect.hideList();
    },
  },
};
</script>

<style lang="scss" scoped>
.stream-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #757575;
}

.checkbox-left {
  margin-left: auto;
}

.text-label {
  color: $ds-denim-9;
}

.input-select {
  :deep(.list) {
    .list-body {
      max-height: 200px;
    }
  }

  :deep(.list-option) {
    padding-right: 8px;

    label.checkbox {
      margin: 0;
    }
  }
}

.intersection {
  height: 34px;
  width: 100%;
}
</style>
